<template>
  <Teleport to="body">
    <div v-if="open" class="admin-detail-overlay" @click.self="$emit('close')">
      <div class="admin-detail-panel glass-panel" role="dialog" aria-modal="true">
        <header class="detail-header">
          <div>
            <h2>{{ displayName }}</h2>
            <p class="detail-email">{{ detail?.email }}</p>
          </div>
          <button type="button" class="btn-close" @click="$emit('close')" aria-label="Close">&times;</button>
        </header>

        <div v-if="loading" class="detail-loading">
          <i class="fas fa-spinner fa-spin"></i> Loading…
        </div>

        <template v-else-if="detail">
          <section class="detail-section">
            <h3>Profile</h3>
            <div class="detail-grid">
              <div><span class="label">Resume</span>{{ detail.hasResume ? 'Uploaded' : 'Not uploaded' }}</div>
              <div><span class="label">Credits</span>{{ detail.credits }}</div>
              <div><span class="label">Jobs</span>{{ detail.jobCount }}</div>
              <div><span class="label">Generated</span>{{ detail.resumesGenerated }}</div>
              <div><span class="label">Signed up</span>{{ formatDate(detail.signupDate) }}</div>
              <div><span class="label">Last login</span>{{ formatDate(detail.lastLogin) }}</div>
            </div>
          </section>

          <section class="detail-section credits-section">
            <h3>Add credits</h3>
            <div class="credits-form">
              <input
                v-model.number="creditAmount"
                type="number"
                min="1"
                class="credits-input"
                placeholder="Amount"
              />
              <input
                v-model="creditReason"
                type="text"
                class="reason-input"
                placeholder="Reason (optional)"
              />
              <button
                type="button"
                class="btn-grant"
                :disabled="granting || !creditAmount"
                @click="grantCredits"
              >
                {{ granting ? 'Saving…' : 'Grant credits' }}
              </button>
            </div>
            <p v-if="creditMessage" class="credit-msg">{{ creditMessage }}</p>
          </section>

          <section class="detail-section">
            <h3>Jobs &amp; resume accuracy</h3>
            <p class="section-hint">Review match scores and resume excerpts to verify the AI stayed grounded in the user&apos;s experience.</p>

            <div v-if="loadingJobs" class="detail-loading small">Loading jobs…</div>
            <div v-else-if="!jobs.length" class="empty">No jobs yet</div>
            <div v-else class="jobs-list">
              <article
                v-for="job in jobs"
                :key="job._id"
                class="job-card"
                :class="{ expanded: expandedJobId === job._id }"
              >
                <button type="button" class="job-card-head" @click="toggleJob(job._id)">
                  <div class="job-head-text">
                    <strong>{{ job.title }}</strong>
                    <span>{{ job.companyName }}</span>
                  </div>
                  <div class="job-head-meta">
                    <span class="status-pill">{{ formatStatus(job.status) }}</span>
                    <span
                      v-if="job.compatibilityScore"
                      class="match-pill"
                    >{{ job.compatibilityScore.overallScore }}% · {{ job.compatibilityScore.overallVerdict }}</span>
                    <i class="fas" :class="expandedJobId === job._id ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
                  </div>
                </button>

                <div v-if="expandedJobId === job._id" class="job-card-body">
                  <div v-if="job.compatibilityScore" class="compat-block">
                    <h4>Compatibility</h4>
                    <ul v-if="job.compatibilityScore.highlights?.length" class="bullet-list good">
                      <li v-for="(h, i) in job.compatibilityScore.highlights" :key="'h-' + i">{{ h }}</li>
                    </ul>
                    <ul v-if="job.compatibilityScore.gaps?.length" class="bullet-list warn">
                      <li v-for="(g, i) in job.compatibilityScore.gaps" :key="'g-' + i">{{ g }}</li>
                    </ul>
                  </div>
                  <div v-if="job.resumeTextPreview" class="resume-block">
                    <h4>Generated resume excerpt</h4>
                    <pre class="resume-excerpt">{{ job.resumeTextPreview }}</pre>
                    <p class="excerpt-meta">{{ job.resumeTextLength }} characters total</p>
                  </div>
                  <p v-else-if="!job.hasResume" class="muted">No tailored resume generated yet.</p>
                </div>
              </article>
            </div>
          </section>
        </template>
      </div>
    </div>
  </Teleport>
</template>

<script>
import axios from '@/axios';
import { formatApiErrorMessage } from '@/axios';
import { statusLabel } from '@/utils/jobStatus';

export default {
  name: 'AdminUserDetailPanel',
  props: {
    open: { type: Boolean, default: false },
    userId: { type: String, default: null },
  },
  emits: ['close', 'credits-updated'],
  data() {
    return {
      loading: false,
      loadingJobs: false,
      detail: null,
      jobs: [],
      expandedJobId: null,
      creditAmount: 10,
      creditReason: '',
      granting: false,
      creditMessage: null,
    };
  },
  computed: {
    displayName() {
      if (!this.detail) return 'User';
      const n = [this.detail.firstName, this.detail.lastName].filter(Boolean).join(' ').trim();
      return n || this.detail.email || 'User';
    },
  },
  watch: {
    open(val) {
      if (val && this.userId) this.load();
      if (!val) this.reset();
    },
    userId(val) {
      if (this.open && val) this.load();
    },
  },
  methods: {
    formatStatus: statusLabel,
    reset() {
      this.detail = null;
      this.jobs = [];
      this.expandedJobId = null;
      this.creditMessage = null;
    },
    async load() {
      this.loading = true;
      this.loadingJobs = true;
      try {
        const [detailRes, jobsRes] = await Promise.all([
          axios.get(`/api/admin/users/${this.userId}`),
          axios.get(`/api/admin/users/${this.userId}/jobs`, {
            params: { includeResumeText: true },
          }),
        ]);
        this.detail = detailRes.data;
        this.jobs = jobsRes.data.jobs || [];
      } catch (err) {
        alert(formatApiErrorMessage(err, 'Could not load user'));
        this.$emit('close');
      } finally {
        this.loading = false;
        this.loadingJobs = false;
      }
    },
    toggleJob(id) {
      this.expandedJobId = this.expandedJobId === id ? null : id;
    },
    formatDate(iso) {
      if (!iso) return '—';
      return new Date(iso).toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
    },
    async grantCredits() {
      const amount = parseInt(this.creditAmount, 10);
      if (!amount || amount < 1) return;
      this.granting = true;
      this.creditMessage = null;
      try {
        const res = await axios.patch(`/api/admin/users/${this.userId}/credits`, {
          amount,
          reason: this.creditReason.trim() || undefined,
        });
        this.detail.credits = res.data.creditsAfter;
        this.creditMessage = `Credits updated: ${res.data.creditsBefore} → ${res.data.creditsAfter}`;
        this.$emit('credits-updated');
      } catch (err) {
        this.creditMessage = formatApiErrorMessage(err, 'Failed to grant credits');
      } finally {
        this.granting = false;
      }
    },
  },
};
</script>

<style scoped>
.admin-detail-overlay {
  position: fixed;
  inset: 0;
  background: var(--app-overlay, rgba(0, 0, 0, 0.65));
  z-index: 1000;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 24px 16px;
  overflow-y: auto;
}

.admin-detail-panel {
  width: 100%;
  max-width: 720px;
  background: var(--surface-2);
  border: 1px solid var(--border);
  border-radius: 16px;
  padding: 24px;
  margin: auto;
}

.detail-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  margin-bottom: 20px;
}

.detail-header h2 {
  margin: 0;
  font-size: 22px;
}

.detail-email {
  margin: 4px 0 0;
  color: var(--text-secondary);
  font-size: 14px;
}

.btn-close {
  border: none;
  background: none;
  font-size: 28px;
  line-height: 1;
  cursor: pointer;
  color: var(--text-secondary);
}

.detail-section {
  margin-bottom: 24px;
}

.detail-section h3 {
  margin: 0 0 12px;
  font-size: 16px;
}

.section-hint {
  font-size: 13px;
  color: var(--text-secondary);
  margin: 0 0 12px;
}

.detail-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  gap: 12px;
  font-size: 14px;
}

.detail-grid .label {
  display: block;
  font-size: 11px;
  color: var(--text-muted);
  text-transform: uppercase;
  margin-bottom: 2px;
}

.credits-form {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.credits-input {
  width: 100px;
  padding: 8px 12px;
  border-radius: 8px;
  border: 1px solid var(--input-border);
  background: var(--input-bg);
  color: var(--text-primary);
}

.reason-input {
  flex: 1;
  min-width: 160px;
  padding: 8px 12px;
  border-radius: 8px;
  border: 1px solid var(--input-border);
  background: var(--input-bg);
  color: var(--text-primary);
}

.btn-grant {
  padding: 8px 16px;
  border-radius: 8px;
  border: none;
  background: var(--primary);
  color: var(--primary-foreground);
  cursor: pointer;
}

.btn-grant:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.credit-msg {
  margin: 8px 0 0;
  font-size: 13px;
  color: var(--success);
}

.detail-loading {
  text-align: center;
  padding: 32px;
  color: var(--text-muted);
}

.detail-loading.small {
  padding: 16px;
}

.jobs-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.job-card {
  border: 1px solid var(--border);
  border-radius: 10px;
  overflow: hidden;
}

.job-card-head {
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  padding: 12px 14px;
  border: none;
  background: var(--surface-3);
  cursor: pointer;
  text-align: left;
  color: inherit;
}

.job-head-text {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.job-head-text span {
  font-size: 12px;
  color: var(--text-secondary);
}

.job-head-meta {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.status-pill,
.match-pill {
  font-size: 11px;
  padding: 2px 8px;
  border-radius: 999px;
  background: var(--surface-2);
  border: 1px solid var(--border);
}

.job-card-body {
  padding: 14px;
  font-size: 13px;
}

.bullet-list {
  margin: 0 0 12px;
  padding-left: 18px;
}

.bullet-list.good {
  color: var(--success);
}

.bullet-list.warn {
  color: var(--text-secondary);
}

.resume-excerpt {
  white-space: pre-wrap;
  word-break: break-word;
  max-height: 200px;
  overflow-y: auto;
  font-size: 12px;
  background: var(--surface-3);
  padding: 12px;
  border-radius: 8px;
  margin: 0;
}

.excerpt-meta {
  margin: 6px 0 0;
  font-size: 11px;
  color: var(--text-muted);
}

.muted,
.empty {
  color: var(--text-muted);
  font-size: 13px;
}
</style>
